import { getState, subscribe } from '../../../state.js';
import { collectCurrentListing } from '../../../listing-utils.js';
import { copyText } from '../../../utils.js';
import { buildKeywordUsageModel } from './keyword-usage-model.js';
import {
  hydrateKeywordTranslations,
  ensureKeywordTranslations,
  getCachedKeywordTranslation,
} from './keyword-translation.js';

const PAGE_SIZE = 12;
const POLICY_TONES = new Set([
  'must_use',
  'can_use',
  'backend_only',
  'ads_only',
  'forbidden',
  'selected',
]);

function createKeywordShowcaseElement(tagName, className = '', text = undefined) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined && text !== null) element.textContent = String(text);
  return element;
}

function formatNumber(value) {
  if (value === null || value === undefined) return '暂无数据';
  return new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2 }).format(Number(value));
}

function formatPercent(value) {
  if (value === null || value === undefined) return '暂无数据';
  const numeric = Number(value);
  const percent = numeric <= 1 ? numeric * 100 : numeric;
  return `${new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2 }).format(percent)}%`;
}

function formatScore(value) {
  if (value === null || value === undefined) return '暂无数据';
  return new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 1 }).format(Number(value));
}

function formatRelevanceScore(value) {
  if (value === null || value === undefined) return '暂无数据';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '暂无数据';
  const percent = numeric >= 0 && numeric <= 1 ? numeric * 100 : numeric;
  return `${new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 1 }).format(percent)}%`;
}

function copySafeCell(value) {
  return String(value ?? '').replace(/[\t\r\n]+/g, ' ').trim();
}

function keywordColumnText(rows) {
  return rows
    .map((row) => copySafeCell(row?.keyword))
    .filter(Boolean)
    .join('\n');
}

function placementLabel(row) {
  if (!row.placements.length) {
    return row.plannedFields.length ? `未使用；原规划：${row.plannedFields.join('、')}` : '未使用';
  }
  return row.placements.map((placement) => {
    if (placement.matchType === 'backend_tokenized') return `${placement.label}（分词匹配）`;
    if (placement.matchType === 'distributed_tokens') return `${placement.label}（词元覆盖）`;
    return placement.label;
  }).join('、');
}

function evidenceText(row) {
  const parts = [];
  if (row.sources.length) parts.push(`来源：${row.sources.join('、')}`);
  if (row.plannedFields.length) parts.push(`生成规划：${row.plannedFields.join('、')}`);
  if (row.sourceAsins.length) parts.push(`参考 ASIN：${row.sourceAsins.join('、')}`);
  if (row.metrics.searches30d !== null) parts.push(`30 天搜索量：${formatNumber(row.metrics.searches30d)}`);
  if (row.metrics.relevanceScore !== null) {
    parts.push(`系统相关性：${formatRelevanceScore(row.metrics.relevanceScore)}`);
  }
  if (row.metrics.purchaseRate !== null) parts.push(`购买率：${formatPercent(row.metrics.purchaseRate)}`);
  if (row.metrics.competition !== null) parts.push(`竞争商品数：${formatNumber(row.metrics.competition)}`);
  if (row.metrics.cpc !== null) parts.push(`CPC：${formatNumber(row.metrics.cpc)}`);
  if (row.metrics.supplyDemandRatio !== null) {
    parts.push(`供需比：${formatNumber(row.metrics.supplyDemandRatio)}`);
  }
  if (row.metrics.policyScore !== null) parts.push(`使用优先级：${formatScore(row.metrics.policyScore)}`);
  return parts.length ? parts.join('；') : '没有保存可核验的市场指标或来源记录';
}

export function buildKeywordCopyText(model, scope = 'report') {
  const usedRows = model?.usedRows || model?.rows || [];
  const unusedRows = model?.unusedRows || [];
  if (scope === 'used') return keywordColumnText(usedRows);
  if (scope === 'unused') return keywordColumnText(unusedRows);
  if (scope === 'all') return keywordColumnText([...usedRows, ...unusedRows]);

  const rows = [...usedRows, ...unusedRows];
  if (!rows.length) return '';
  const header = [
    '关键词',
    '中文',
    '30天搜索量',
    '系统相关性',
    '购买率',
    '竞争商品数',
    'CPC',
    '供需比',
    '状态 / 位置',
    '原因',
    '依据',
  ].join('\t');
  const body = rows.map((row) => [
    row.keyword,
    getCachedKeywordTranslation(row.keyword),
    formatNumber(row.metrics.searches30d),
    formatRelevanceScore(row.metrics.relevanceScore),
    formatPercent(row.metrics.purchaseRate),
    formatNumber(row.metrics.competition),
    formatNumber(row.metrics.cpc),
    formatNumber(row.metrics.supplyDemandRatio),
    `${row.statusLabel}；${placementLabel(row)}`,
    row.reason,
    evidenceText(row),
  ].map(copySafeCell).join('\t'));
  return [header, ...body].join('\n');
}

function createCopyButton(label, scope, text = '') {
  const button = createKeywordShowcaseElement('button', 'keyword-copy-button', label);
  button.type = 'button';
  button.dataset.keywordCopy = scope;
  if (text) button.dataset.keywordCopyText = text;
  return button;
}

function createSummaryItem(label, value, tone = '') {
  const item = createKeywordShowcaseElement(
    'span',
    `keyword-showcase-summary-item${tone ? ` is-${tone}` : ''}`,
  );
  item.append(
    createKeywordShowcaseElement('strong', '', value),
    createKeywordShowcaseElement('span', '', label),
  );
  return item;
}

function createUsageBadge(placement) {
  const className = placement.field === 'searchTerms'
    ? 'keyword-placement-badge is-backend'
    : 'keyword-placement-badge is-visible';
  const label = placement.matchType === 'backend_tokenized'
    ? `${placement.label}（分词）`
    : placement.matchType === 'distributed_tokens'
      ? `${placement.label}（词元）`
      : placement.label;
  const badge = createKeywordShowcaseElement('span', className, label);
  if (placement.matchedTokens?.length) {
    badge.title = `匹配词元：${placement.matchedTokens.join('、')}`;
  }
  return badge;
}

function createPolicyBadge(row) {
  const tone = POLICY_TONES.has(row.policy) ? row.policy : 'unclassified';
  return createKeywordShowcaseElement(
    'span',
    `keyword-policy-badge keyword-policy-badge--${tone}`,
    row.policyLabel,
  );
}

function createStatusBadge(row) {
  const statusTone = {
    used: 'used',
    policy_conflict: 'conflict',
    missing_required: 'missing',
    selected_not_used: 'pending',
    planned_not_used: 'pending',
    excluded: 'excluded',
    not_used: 'unused',
  }[row.status] || 'unused';
  return createKeywordShowcaseElement(
    'span',
    `keyword-showcase-status is-${statusTone}`,
    row.statusLabel,
  );
}

function createEvidenceDetails(row) {
  const details = createKeywordShowcaseElement('details', 'keyword-showcase-evidence-details');
  details.append(createKeywordShowcaseElement('summary', '', '查看依据'));
  const body = createKeywordShowcaseElement('div', 'keyword-showcase-evidence-body');

  const facts = [
    ['来源', row.sources.length ? row.sources.join('、') : '未保存'],
    ['生成规划', row.plannedFields.length ? row.plannedFields.join('、') : '无'],
    ['参考 ASIN', row.sourceAsins.length ? row.sourceAsins.join('、') : '无'],
  ];
  for (const [label, value] of facts) {
    const fact = createKeywordShowcaseElement('div', 'keyword-showcase-evidence-fact');
    fact.append(
      createKeywordShowcaseElement('span', '', label),
      createKeywordShowcaseElement('strong', '', value),
    );
    body.append(fact);
  }

  details.append(body);
  return details;
}

function createMarketValueCell(row, metricKey, formatter) {
  const value = row.metrics?.[metricKey];
  return createKeywordShowcaseElement(
    'td',
    `keyword-showcase-market-value-cell is-${metricKey}`,
    value === null || value === undefined ? '—' : formatter(value),
  );
}

function createKeywordTable(rows, group) {
  const scroll = createKeywordShowcaseElement('div', 'keyword-showcase-table-scroll');
  const table = createKeywordShowcaseElement('table', 'keyword-showcase-table');
  const caption = createKeywordShowcaseElement(
    'caption',
    'visually-hidden',
    group === 'used' ? '当前已使用关键词' : '当前未使用关键词',
  );
  const head = createKeywordShowcaseElement('thead');
  const headRow = createKeywordShowcaseElement('tr');
  for (const [label, className] of [
    ['关键词', 'keyword-column'],
    ['中文', 'zh-column'],
    ['30 天搜索量', 'searches-column metric-column'],
    ['系统相关性', 'relevance-column metric-column'],
    ['购买率', 'purchase-column metric-column'],
    ['竞争商品数', 'competition-column metric-column'],
    ['CPC', 'cpc-column metric-column'],
    ['供需比', 'supply-column metric-column'],
    ['状态 / 位置', 'placement-column'],
    ['原因', 'reason-column'],
    ['依据', 'evidence-column'],
  ]) {
    headRow.append(createKeywordShowcaseElement('th', className, label));
  }
  head.append(headRow);

  const body = createKeywordShowcaseElement('tbody');
  for (const row of rows) {
    const tr = createKeywordShowcaseElement(
      'tr',
      row.status === 'policy_conflict' || row.status === 'missing_required'
        ? 'is-attention'
        : '',
    );
    const keywordCell = createKeywordShowcaseElement('td', 'keyword-showcase-keyword-cell');
    keywordCell.append(
      createKeywordShowcaseElement('strong', 'keyword-showcase-term', row.keyword),
      createCopyButton('复制', 'single', row.keyword),
    );

    const zhCell = createKeywordShowcaseElement('td', 'keyword-showcase-zh-cell');
    const zhLine = createKeywordShowcaseElement(
      'span',
      'keyword-showcase-term-zh is-loading',
      '加载中…',
    );
    zhLine.dataset.keywordZhFor = row.keyword;
    zhCell.append(zhLine);

    const placementCell = createKeywordShowcaseElement('td', 'keyword-showcase-placement-cell');
    const statusLine = createKeywordShowcaseElement('div', 'keyword-showcase-status-line');
    statusLine.append(createStatusBadge(row), createPolicyBadge(row));
    if (row.semanticRoleLabel) {
      statusLine.append(createKeywordShowcaseElement(
        'span',
        'keyword-policy-badge keyword-policy-badge--role',
        row.semanticRoleLabel,
      ));
    }
    placementCell.append(statusLine);
    const placements = createKeywordShowcaseElement('div', 'keyword-showcase-placements');
    if (row.placements.length) {
      placements.append(...row.placements.map(createUsageBadge));
    } else {
      placements.append(createKeywordShowcaseElement(
        'span',
        'keyword-showcase-planned',
        row.plannedFields.length ? `原规划：${row.plannedFields.join('、')}` : '当前没有写入位置',
      ));
    }
    placementCell.append(placements);

    const reasonCell = createKeywordShowcaseElement('td', 'keyword-showcase-reason-cell');
    reasonCell.append(createKeywordShowcaseElement('p', '', row.reason));

    const evidenceCell = createKeywordShowcaseElement('td', 'keyword-showcase-evidence-cell');
    evidenceCell.append(createEvidenceDetails(row));
    tr.append(
      keywordCell,
      zhCell,
      createMarketValueCell(row, 'searches30d', formatNumber),
      createMarketValueCell(row, 'relevanceScore', formatRelevanceScore),
      createMarketValueCell(row, 'purchaseRate', formatPercent),
      createMarketValueCell(row, 'competition', formatNumber),
      createMarketValueCell(row, 'cpc', formatNumber),
      createMarketValueCell(row, 'supplyDemandRatio', formatNumber),
      placementCell,
      reasonCell,
      evidenceCell,
    );
    body.append(tr);
  }
  table.append(caption, head, body);
  scroll.append(table);
  return scroll;
}

function createPagination(group, total, page, pageCount) {
  if (pageCount <= 1) return null;
  const nav = createKeywordShowcaseElement('nav', 'keyword-showcase-pagination');
  nav.setAttribute('aria-label', `${group === 'used' ? '已使用' : '未使用'}关键词分页`);
  const previous = createKeywordShowcaseElement('button', '', '上一页');
  previous.type = 'button';
  previous.dataset.keywordPage = group;
  previous.dataset.page = String(page - 1);
  previous.disabled = page <= 1;
  const next = createKeywordShowcaseElement('button', '', '下一页');
  next.type = 'button';
  next.dataset.keywordPage = group;
  next.dataset.page = String(page + 1);
  next.disabled = page >= pageCount;
  nav.append(
    previous,
    createKeywordShowcaseElement('span', '', `${page} / ${pageCount} · 共 ${total} 个`),
    next,
  );
  return nav;
}

function createKeywordGroup(rows, group, viewState) {
  const isUsed = group === 'used';
  const pageKey = isUsed ? 'usedPage' : 'unusedPage';
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const page = Math.min(Math.max(1, Number(viewState[pageKey]) || 1), pageCount);
  viewState[pageKey] = page;
  const start = (page - 1) * PAGE_SIZE;
  const visibleRows = rows.slice(start, start + PAGE_SIZE);
  const title = isUsed
    ? `已使用关键词（${rows.length}）`
    : `未使用与排除（${rows.length}）`;
  const helper = isUsed
    ? '位置来自当前成稿实时匹配；同一关键词可能同时出现在前台和 Search Terms。'
    : '只列出有策略、选词或生成规划依据的候选，说明没有采用的原因。';

  const content = createKeywordShowcaseElement('div', 'keyword-showcase-section-content');
  content.append(
    createKeywordShowcaseElement('p', 'keyword-showcase-section-help', helper),
    createKeywordTable(visibleRows, group),
  );
  const pagination = createPagination(group, rows.length, page, pageCount);
  if (pagination) content.append(pagination);

  if (isUsed) {
    const section = createKeywordShowcaseElement('section', 'keyword-showcase-section is-used');
    section.append(createKeywordShowcaseElement('h3', '', title), content);
    return section;
  }

  const details = createKeywordShowcaseElement('details', 'keyword-showcase-section is-unused');
  details.open = Boolean(viewState.unusedOpen);
  const summary = createKeywordShowcaseElement('summary');
  summary.append(
    createKeywordShowcaseElement('strong', '', title),
    createKeywordShowcaseElement(
      'span',
      '',
      `${viewState.unusedOpen ? '收起' : '展开'}查看未采用原因`,
    ),
  );
  details.append(summary, content);
  details.addEventListener('toggle', () => {
    viewState.unusedOpen = details.open;
    const hint = summary.querySelector('span');
    if (hint) hint.textContent = `${details.open ? '收起' : '展开'}查看未采用原因`;
  });
  return details;
}

function createKeywordShowcaseEmptyState(message) {
  const empty = createKeywordShowcaseElement('div', 'keyword-showcase-empty');
  empty.append(
    createKeywordShowcaseElement('strong', '', '暂无可追溯关键词'),
    createKeywordShowcaseElement('p', '', message),
  );
  return empty;
}

function createKeywordShowcaseContent(model, viewState) {
  const fragment = document.createDocumentFragment();
  const toolbar = createKeywordShowcaseElement('div', 'keyword-showcase-toolbar');
  const summary = createKeywordShowcaseElement('div', 'keyword-showcase-summary-strip');
  summary.append(
    createSummaryItem('已使用', model.summary.usedCount, 'used'),
    createSummaryItem('未使用', model.summary.unusedCount, 'unused'),
    createSummaryItem('前台可见', model.summary.visibleCount),
    createSummaryItem('Search Terms', model.summary.backendCount),
  );
  if (model.summary.riskCount || model.summary.missingRequiredCount) {
    summary.append(createSummaryItem(
      '需关注',
      model.summary.riskCount + model.summary.missingRequiredCount,
      'attention',
    ));
  }

  const actions = createKeywordShowcaseElement('div', 'keyword-showcase-copy-actions');
  const usedCopy = createCopyButton('复制已用关键词列', 'used');
  usedCopy.disabled = !model.usedRows.length;
  const unusedCopy = createCopyButton('复制未用关键词列', 'unused');
  unusedCopy.disabled = !model.unusedRows.length;
  const allCopy = createCopyButton('复制全部关键词列', 'all');
  allCopy.disabled = !model.summary.traceableCount;
  const reportCopy = createCopyButton('复制整个表格', 'report');
  reportCopy.disabled = !model.summary.traceableCount;
  actions.append(usedCopy, unusedCopy, allCopy, reportCopy);
  toolbar.append(summary, actions);
  fragment.append(toolbar);

  const intro = createKeywordShowcaseElement(
    'p',
    'keyword-showcase-intro',
    '按当前 Listing 实时核对“用了什么、没用什么、为什么”；市场数据直接展示，生成依据按需展开。复制关键词列可得到单列，复制整个表格可直接粘贴到 Excel 并自动分列。',
  );
  fragment.append(intro);

  if (!model.summary.traceableCount) {
    fragment.append(createKeywordShowcaseEmptyState(
      '当前文案和生成记录中都没有可追溯关键词；历史 Listing 若未保存词库数据，也会在这里明确降级显示。',
    ));
    return fragment;
  }

  if (model.usedRows.length) {
    fragment.append(createKeywordGroup(model.usedRows, 'used', viewState));
  }
  if (model.unusedRows.length) {
    fragment.append(createKeywordGroup(model.unusedRows, 'unused', viewState));
  }
  return fragment;
}

function collectKeywordShowcaseInput() {
  const listingGenerationMeta = getState('listingGenerationMeta') || {};
  const mergeMeta = listingGenerationMeta.mergeMeta || {};
  const businessContext = getState('businessContext') || mergeMeta.businessMeta || {};
  const statePolicies = getState('keywordUsePolicies');
  const keywordUsePolicies = Array.isArray(statePolicies) && statePolicies.length
    ? statePolicies
    : (businessContext.keywordUsePolicies || []);
  return {
    listing: collectCurrentListing({
      full: true,
      useRevisionSearchTermsDraft: true,
    }),
    keywordLibrary: getState('keywordLibrary'),
    keywordUsePolicies,
    selectedKeywords: getState('selectedStKeywords'),
    keywordPlan: mergeMeta.keywordPlan || businessContext.keywordPlan || null,
    generationLedger: mergeMeta.generationLedger || null,
    keywordAssetPackages: [
      mergeMeta.keywordAssetPackage,
      businessContext.keywordAssetPackage,
    ].filter(Boolean),
    listingDraftV1: getState('listingDraftV1'),
    searchTermsSelectionLedger: mergeMeta.searchTermsSelectionLedger || null,
    optimizerResult: getState('optimizerResult'),
  };
}

export function renderKeywordShowcaseInto(container, input, { viewState = {} } = {}) {
  if (!container) return null;
  const model = buildKeywordUsageModel(input);
  container.replaceChildren(createKeywordShowcaseContent(model, viewState));
  // 翻译补水异步进行，网络请求集中在 keyword-translation.js，不阻塞渲染；
  // 传入整表关键词，翻页和复制整表都能直接命中缓存。
  hydrateKeywordTranslations(container, [
    ...(model.usedRows || []),
    ...(model.unusedRows || []),
  ].map((row) => row.keyword));
  const summary = document.getElementById('keyword-showcase-summary');
  if (summary) {
    summary.textContent = model.summary.traceableCount
      ? `${model.summary.usedCount} 已用 · ${model.summary.unusedCount} 未用`
      : '暂无可追溯关键词';
  }
  const liveStatus = document.getElementById('keyword-showcase-live-status');
  if (liveStatus) {
    liveStatus.textContent = model.summary.traceableCount
      ? `关键词展示已更新：${model.summary.usedCount} 个已使用，${model.summary.unusedCount} 个未使用。`
      : '关键词展示已更新，暂无可追溯关键词。';
  }
  return model;
}

export function initKeywordShowcase() {
  const container = document.getElementById('keyword-showcase-root');
  if (!container) return;
  const details = document.getElementById('keyword-showcase-details');
  const summary = document.getElementById('keyword-showcase-summary');
  const liveStatus = document.getElementById('keyword-showcase-live-status');
  const viewState = {
    usedPage: 1,
    unusedPage: 1,
    unusedOpen: false,
  };
  let currentInput = null;
  let currentModel = null;
  let dirty = true;
  let scheduled = false;
  let hasRendered = false;
  const scheduleFrame = typeof requestAnimationFrame === 'function'
    ? requestAnimationFrame
    : (callback) => setTimeout(callback, 0);
  const renderCurrent = () => {
    currentInput = collectKeywordShowcaseInput();
    currentModel = renderKeywordShowcaseInto(container, currentInput, { viewState });
    return currentModel;
  };
  const scheduleRender = () => {
    dirty = true;
    viewState.usedPage = 1;
    viewState.unusedPage = 1;
    if (details && !details.open) {
      if (hasRendered && summary) summary.textContent = '内容已更新 · 展开查看';
      return;
    }
    if (scheduled) return;
    scheduled = true;
    scheduleFrame(() => {
      scheduled = false;
      if (!dirty || (details && !details.open)) return;
      dirty = false;
      hasRendered = true;
      renderCurrent();
    });
  };

  container.addEventListener('click', async (event) => {
    const pageButton = event.target.closest('[data-keyword-page]');
    if (pageButton) {
      const group = pageButton.dataset.keywordPage;
      const pageKey = group === 'unused' ? 'unusedPage' : 'usedPage';
      viewState[pageKey] = Math.max(1, Number(pageButton.dataset.page) || 1);
      if (currentInput) currentModel = renderKeywordShowcaseInto(container, currentInput, { viewState });
      return;
    }

    const copyButton = event.target.closest('[data-keyword-copy]');
    if (!copyButton || copyButton.disabled) return;
    const originalLabel = copyButton.textContent;
    const scope = copyButton.dataset.keywordCopy;
    let value;
    if (scope === 'single') {
      value = copyButton.dataset.keywordCopyText;
    } else {
      if (scope === 'report' && currentModel) {
        // 复制整表包含中文列，先确保所有关键词译文就绪（命中缓存时不发请求）。
        copyButton.textContent = '准备中文…';
        await ensureKeywordTranslations([
          ...(currentModel.usedRows || []),
          ...(currentModel.unusedRows || []),
        ].map((row) => row.keyword));
      }
      value = buildKeywordCopyText(currentModel, scope);
    }
    if (!value) return;
    try {
      await copyText(value);
      copyButton.textContent = '已复制';
      if (liveStatus) liveStatus.textContent = `${originalLabel}已复制到剪贴板。`;
    } catch (error) {
      copyButton.textContent = '复制失败';
      if (liveStatus) liveStatus.textContent = `复制失败：${error?.message || '浏览器未授权剪贴板访问'}`;
    }
    setTimeout(() => {
      if (copyButton.isConnected) copyButton.textContent = originalLabel;
    }, 1600);
  });

  for (const key of [
    'listingDraftTitle',
    'listingDraftBullets',
    'listingDraftDescription',
    'listingDraftSearchTerms',
    'listingDraftQa',
    'listingDraftV1',
    'revisionSearchTermsDraft',
    'keywordLibrary',
    'businessContext',
    'keywordUsePolicies',
    'selectedStKeywords',
    'listingGenerationMeta',
    'optimizerResult',
  ]) {
    subscribe(`state:${key}Changed`, scheduleRender);
  }
  if (details) {
    details.addEventListener('toggle', () => {
      if (details.open && dirty) scheduleRender();
    });
  }
  scheduleRender();
}
